import { mount, unmount, setChildren, el } from 'https://cdn.venturz.co/lib/redom.es.js';

import EventSystem from 'https://cdn.venturz.co/modules/event-system/index.js';
import SystemDefinedUserStatusTracker from 'https://cdn.venturz.co/modules/system-defined-user-status-tracker/index.js';
import SelfDestructingMessage from 'https://cdn.venturz.co/modules/self-destructing-message/index.js';
import UserObserver from 'https://cdn.venturz.co/modules/user-observer/index.js';
import ScreenSizeDetector from 'https://cdn.venturz.co/modules/screen-size-detector/index.js';
import cookieReader from '/programs/cookie/reader.js';
import cookieWriter from '/programs/cookie/writer.js';
import dependencyNames from '/programs/constants/dependency-names.js';

import Indicators from './indicators.js';
import './spinners.js';

window.Notifier = SelfDestructingMessage;

class Page {

  constructor(startup, user, pageSettings, configuration, functionalModules) {

    this.apiInterface = null;
    this.userObserver = null;
    this.eventSystem = null;
    this.tableOfContents = null;
    this.authorizationToken = null;
    this.authorizationValid = false;
    this.user = user;
    this.viewer = null;
    this.coreDependenciesReady = false;

    this.userInitialized = false;
    this.startup = startup;
    this.pageSettings = pageSettings;
    this.configuration = configuration;
    this.sharedAssetsVersion = configuration.sharedAssetsVersion;
    this.cdnVersion = configuration.venturzCDNVersion;
    this.functionalModules = functionalModules;
    this.url = new URL(window.location.href);
    this.indicators = new Indicators();
    this.screenSizeDetector = new ScreenSizeDetector();
    this.performanceEndpoint = '/__/metrics/app-performance';

    this.dependencies = {};
    this.cssDependencies = {};
    this.moduleJavaScriptResolvers = new Map();
    this.moduleResolvers = new Map();

    this.moduleInstances = [];

    window.page = this;

  }

  async getDependency(name) {

    const dependency = this.dependencies[name];

    if (dependency !== void 0) {
      return dependency;
    }

    if (name === dependencyNames.API_INTERFACE) {

      await this.loadAndInitializeAPIInterface();

    }

    if (name === dependencyNames.DIALOGS_MANAGER) {

      await this.loadAndInitializeDialogsManager();

    }

    return this.dependencies[name];

  }

  appendVersion(url, version) {

    if (version === void 0 || version === null || version === '') {
      return url;
    }

    return url + (url.includes('?') ? '&' : '?') + 'v=' + version;

  }

  versionedUrl(url) {

    if (url.startsWith('https://cdn.venturz.')) {
      return this.appendVersion(url, window.__ASSETS__?.venturzCDN ?? (new Date()).toISOString().split('T').shift());
    }

    return this.appendVersion(url, window.__ASSETS__?.sharedAssets ?? (new Date()).toISOString().split('T').shift());

  }

  importModule(url) {

    return import(this.versionedUrl(url));

  }

  async resolveModule(moduleName) {

    if (this.moduleJavaScriptResolvers.has(moduleName)) {
      return this.moduleJavaScriptResolvers.get(moduleName);
    }

    const promise = new Promise(function resolver(resolve, reject) {
      const basePath = `${this.configuration.sharedAssetsServer}/programs/${moduleName}`;
      this.importModule(`${basePath}/index.js`).then(function onSuccess(m) {
        resolve(m.default);
      }, reject);
    }.bind(this));

    this.moduleJavaScriptResolvers.set(moduleName, promise);

    return promise;

  }

  async _doInitializeModule(functionalModule) {

    const start = performance.now();

    try {
      console.debug(`Loading ${functionalModule.type}`);
      const Mod = await this.resolveModule(functionalModule.type);
      console.debug(`Loaded ${functionalModule.type} in ${performance.now() - start}`);
      const options = {
        ...functionalModule.options,
        ...functionalModule.properties || {},
        options: functionalModule.options
      };
      const basePath = `${this.configuration.sharedAssetsServer}/programs/${functionalModule.type}`;
      const moduleInstance = new Mod(options, this);
      moduleInstance.externalStyleSheets?.map(function mapper(styleSheet) {
        this.importCSS(styleSheet);
      }.bind(this));
      this.importCSS(this.versionedUrl(`${basePath}/index.css`));
      this.moduleInstances.push(moduleInstance);

      await moduleInstance.initialize();

      return moduleInstance;

    } catch (exception) {

      // eslint-disable-next-line no-console
      console.debug(exception);
      return null;

    } finally {
      console.debug(`Initialized ${functionalModule.type} in ${performance.now() - start}`);
    }

  }

  async initializeModule(functionalModule) {

    const moduleId = functionalModule.properties.id;

    if (this.moduleResolvers.has(moduleId)) {
      return this.moduleResolvers.get(moduleId);
    }

    const promise = new Promise(function resolver(resolve) {

      this._doInitializeModule(functionalModule).then(resolve).catch(resolve);

    }.bind(this));

    this.moduleResolvers.set(moduleId, promise);

    return promise;

  }

  async initializeBlockLevelModules() {

    const functionalModules = this.functionalModules;

    const promises = [];

    for (let functionalModule of functionalModules) {

      const promise = this.initializeModule(functionalModule);

      promises.push(promise);

    }

    return Promise.all(promises);

  }

  async tryInitializeTableOfContents() {

    if (this.pageSettings.enableTableOfContents === false) {
      return;
    }

    const TableOfContents = (await this.importModule('/programs/table-of-contents/index.js')).default;

    this.tableOfContents = new TableOfContents(this);

    this.tableOfContents.initialize();

  }

  async initializePageLevelModules() {

    await this.tryInitializeTableOfContents();

  }

  async tryInitializeReception() {

    if (this.pageSettings.enableReception === false) {
      return;
    }

    await import(`${window.location.origin}/_helpdesk/index.js`);

    try {

      // eslint-disable-next-line no-undef
      await initializeReception(this);

    } catch (exception) {

      console.debug(exception);

    }

  }

  async initializeModules() {

    await this.initializeBlockLevelModules();
    performance.mark('block level modules ready');
    console.debug(performance.measure('block level modules ready', 'observer ready', 'block level modules ready'));
    await this.initializePageLevelModules();
    performance.mark('page level modules ready');
    console.debug(performance.measure('page level modules ready', 'block level modules ready', 'page level modules ready'));
    await this.tryInitializeReception();
    performance.mark('reception ready');
    console.debug(performance.measure('reception ready', 'page level modules ready', 'reception ready'));

  }

  async loadAndInitializeAPIInterface() {

    const APIInterface = (await this.importModule('/programs/api-interface/index.js')).default;

    this.dependencies[dependencyNames.API_INTERFACE] = this.initializeAPIInterface(APIInterface);

  }

  async loadAndInitializeDialogsManager() {

    const DialogsManager = (await this.importModule('/programs/dialogs-manager/index.js')).default;

    this.dependencies[dependencyNames.DIALOGS_MANAGER] = new DialogsManager(this);

  }

  initializeAPIInterface(APIInterface) {

    const startupId = this.startup._id;
    const apiURL = this.configuration.apiURL;

    return new APIInterface(startupId, apiURL, this.getAuthorizationToken(), this.eventSystem, window.isBot !== true);

  }

  initializeContext() {

    this.authorizationToken = cookieReader.get('authorizationToken');

  }

  async startObserver() {

    await this.userObserver.start();

    window.dispatchEvent(new CustomEvent('UserObserverInitialized', {
      observerId: this.userObserver.getObserverId(),
      detail: {
        page: this
      }
    }));
  }

  async initializeUserObserver() {

    if (window.isBot === true) {
      return;
    }

    this.userObserver = new UserObserver(true);

  }

  handleServerEvent(event) {

    this.eventSystem.internal.emit(event.name, event.payload);

  }

  setUserData(data) {

    this.startup = data.startup;
    this.authorization = data.authorization;
    this.user = this.viewer = data.user;
    this.authorizationValid = data.authorizationValid;

  }

  async connect() {

    if (window.isBot === true) {
      return;
    }

    this.eventSystem = new EventSystem();
    const onEvent = this.handleServerEvent.bind(this);
    const data = await await this.eventSystem.initializeWebSocketClient({
      url: '/__nativesocket',
      version: 'v1',
      onConnected() { console.debug('Connected'); },
      onDisconnected() { console.debug('Disconnected'); },
      onEvent,
      onAction(...parameters) { console.debug(parameters, 'Action'); },
      onServerCallWhenOffline() { console.debug('Server call when offline'); },
      onReconnected: this.setUserData.bind(this)
    });

    this.setUserData(data);

  }

  async tryInitializeUser() {

    if (this.userInitialized === true) {
      return this.user;
    }

    const apiInterface = await this.getDependency(dependencyNames.API_INTERFACE);

    try {

      this.user = await apiInterface.getUserByToken();

      await apiInterface.initializeUserInterfaces(this.user);

    } catch (exception) {

      this.user = null;

    } finally {
      this.userInitialized = true;
    }

    return this.user;

  }

  async tryInitializeUserV2() {

    const apiInterface = await this.getDependency(dependencyNames.API_INTERFACE);

    try {

      await apiInterface.initializeUserInterfaces(this.viewer);

    } finally {
      this.userInitialized = true;
    }

    return this.viewer;

  }

  async tryInitializeOnlineStatusTracker() {

    if (window.isBot === true) {
      return;
    }

    this.onlineStatusTracker = new SystemDefinedUserStatusTracker();

    if (this.user === null) {
      this.onlineStatusTracker.initializeWithObserverId(this.configuration.apiURL, this.startup._id, this.userObserver.getObserverId());
    } else {
      this.onlineStatusTracker.initialize(this.configuration.apiURL, this.startup._id, this.user._id, this.getAuthorizationToken());
    }

  }

  async getCookieConsent(options) {

    const page = this;
    const CookieModulesConsentWidget = (await this.importModule('../cookie-modules-consent-widget/index.js')).default;

    return new Promise(function resolver(resolve) {

      const cookieModulesConsentWidget = new CookieModulesConsentWidget(page, options, {
        onComplete() {
          unmount(document.body, cookieModulesConsentWidget);
          resolve();
        }
      });

      mount(document.body, cookieModulesConsentWidget);

    });

  }

  async tryInitializeCookieModules() {

    if (this.startup.gdprEnabled === false) {
      await this.startObserver();
      return;
    }

    await this.tryInitializeUserV2();

    const apiInterface = await this.getDependency(dependencyNames.API_INTERFACE);

    let [cookieModuleConsents, cookieModules] = await Promise.all([
      apiInterface.user.getCookieModuleConsents(),
      apiInterface.getCookieModules()
    ]);

    if (cookieModules.length === 0) {
      return;
    }

    const unsetModules = cookieModuleConsents.filter(function filter(consent) {
      return consent.decision === 'unset';
    });

    const grantedModules = cookieModuleConsents.filter(function filter(consent) {
      return consent.decision !== 'denied';
    });
    const grantedModuleIds = grantedModules.map(function m(mod) {
      return mod.cookieModuleId;
    });

    const CookieModulesManager = (await this.importModule('../cookie-modules-manager/index.js')).default;
    CookieModulesManager.setupGrantedModules({ grantedConsents: grantedModules, cookieModules });

    const analyticsModule = cookieModules.find(function finder(mod) {
      return mod.name === 'Analytics' && mod.system === true;
    });

    if (grantedModuleIds.includes(analyticsModule?._id) === true) {
      await this.startObserver();
    }

    let deniedModules = [];

    if (unsetModules.length > 0) {

      cookieModuleConsents = await this.getCookieConsent({
        cookieModuleConsents,
        cookieModules
      });

      deniedModules = cookieModuleConsents.filter(function filter(consent) {
        return consent.decision === 'denied';
      });
    }

    const newlyGrantedModules = cookieModuleConsents.filter(function filter(consent) {
      return consent.decision === 'granted' && grantedModuleIds.includes(consent.cookieModuleId) === false;
    });

    const deniedModuleIds = deniedModules.map(function m(mod) {
      return mod.cookieModuleId;
    });
    const newlyGrantedModuleIds = newlyGrantedModules.map(function m(mod) {
      return mod.cookieModuleId;
    });

    CookieModulesManager.setupGrantedModules({ grantedConsents: newlyGrantedModules, cookieModules });
    CookieModulesManager.teardownDeniedModules({ deniedConsents: deniedModules, cookieModules });

    if (newlyGrantedModuleIds.includes(analyticsModule?._id) === true) {
      await this.startObserver();
    }

    if (deniedModuleIds.includes(analyticsModule?._id) === true) {
      await this.userObserver.stop();
    }

  }

  async initialize() {

    performance.mark('main loaded');

    this.reportPageLoad();

    await this.connect();
    performance.mark('connected');
    console.debug(performance.measure('connected', 'main loaded', 'connected'));
    this.initializeContext();
    performance.mark('context ready');
    console.debug(performance.measure('context ready', 'connected', 'context ready'));
    this.initializeUserObserver();
    this.coreDependenciesReady = true;
    performance.mark('observer ready');
    console.debug(performance.measure('observer ready', 'context ready', 'observer ready'));
    await this.initializeModules();
    performance.mark('modules ready');
    console.debug(performance.measure('modules ready', 'observer ready', 'modules ready'));
    await this.tryInitializeOnlineStatusTracker();
    console.debug(performance.measure('all', 'modules ready'));

    this.tryInitializeCookieModules();

    const event = new CustomEvent('v:pageready', {
      detail: {
        page: this
      }
    });
    window.dispatchEvent(event);

    this.reportPageInit();

  }

  async importCSS(path) {

    let url = path;

    if (url.startsWith('http') === false) {
      url = import.meta.url.replace('/programs/page/index.js', '') + path;
    }

    if (this.cssDependencies[url] !== void 0) {
      return;
    }

    this.cssDependencies[url] = true;

    const link = document.createElement('link');

    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('href', url);

    document.head.appendChild(link);

  }

  getUserObserverId() {

    return this.userObserver.getObserverId();

  }

  getPageSettings() {

    return this.pageSettings;

  }

  setAuthorizationToken(authorizationToken) {

    this.authorizationToken = authorizationToken;

  }

  async setAuthorization(authorization) {

    const token = authorization.token;

    cookieWriter.set('authorizationToken', token, 30);

    this.setAuthorizationToken(token);

    await this.getAPIInterface().setAuthorization(authorization);

  }

  getUserId() {

    return cookieReader.get('userId');

  }

  getAuthorizationToken() {

    return cookieReader.get('authorizationToken');

  }

  getMainContentContainer() {

    return document.getElementById('main-content-container');

  }

  getPageExtrasContainer() {

    return document.getElementById('page-extras-container');

  }

  getDialogsManager() {

    return this.dependencies[dependencyNames.DIALOGS_MANAGER];

  }

  getAPIInterface() {

    return this.dependencies[dependencyNames.API_INTERFACE];

  }

  getStartup() {

    return this.startup;

  }

  getAuthenticatorAddress() {

    const host = this.startup.address.host;

    if (host.indexOf('localhost') !== -1) {
      return 'http://auth.local.com:3002';
    } else if (host.indexOf('development') !== -1) {
      return 'https://auth.development.venturz.co';
    } else {
      return 'https://auth.venturz.co';
    }

  }

  async reassociateEventSystem() {

    const authorizationToken = this.getAuthorizationToken();

    await this.eventSystem.tryAssociateWithObserverId({
      authorizationToken,
      observerId: this.userObserver.getObserverId()
    });

  }

  deleteQueryParameters(names) {

    for (let name of names) {
      this.url.searchParams.delete(name);
    }

    window.history.replaceState({}, '', this.url);

  }

  reportPageLoad() {

    const data = {
      name: 'page-load',
      duration: performance.now()
    };

    performance.mark('page-load');

    navigator.sendBeacon(this.performanceEndpoint, JSON.stringify(data));

  }

  reportPageInit() {

    const measurement = performance.measure('page-init', 'page-load');
    const data = {
      name: 'page-init',
      duration: measurement.duration
    };
    performance.clearMarks();
    navigator.sendBeacon(this.performanceEndpoint, JSON.stringify(data));

  }

  async showImageInFullView(imageData) {

    const dialogsManager = await this.getDependency(dependencyNames.DIALOGS_MANAGER);
    const ImageFullView = (await this.importModule('/programs/image-full-view/index.js')).default;

    return dialogsManager.display(ImageFullView, imageData);

  }

  async getAssistantClass() {

    const Assistant = (await this.importModule('../assistant/index.js')).default;

    return Assistant;

  }

  async handleComponentClick(component, data) {

    while (this.coreDependenciesReady === false) {
      await new Promise(function resolver(resolve) {
        setTimeout(resolve, 10);
      });
    }

    const functionalModule = this.functionalModules.find(function finder(functionalModule) {
      return functionalModule.properties.id === component.id;
    });

    if (functionalModule === void 0) {
      console.error('No function module found for component: ', component);
      return;
    }

    const moduleInstance = await this.initializeModule(functionalModule);

    if (moduleInstance === null) {
      console.error('Module instance is not created for component: ', component);
    }

    moduleInstance.handlePreInitClick(data);

  }

}

window.Page = Page;

export default Page;